import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { ToastService } from '../../../base/services/toast.service';
import { SpinnerService } from '../../../base/spinner/spinner.service';
import { AuthService } from '../../../base/services/auth.service';
import {
  OrderClient,
  OrderDTO,
  BuyerOrderSummaryDTO,
  SellerOrderSummaryDTO,
  UpdateOrderStatusDTO,
  CancelOrderDTO,
  OrderLimitedDTO,
  FilterDTO,
  PagedResultDTOOfOrderLimitedDTO,
  SortDirection
} from '../../../base/models/models.swagger.generated';
import { ErrorType } from '../../../core/routing/error-types';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  constructor(
    private orderClient: OrderClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private authService: AuthService
  ) { }

  public orderStatusChanged$ = new Subject<void>();

  public getOrder(id: number): Observable<OrderDTO> {
    this.spinnerService.showSpinner = true;
    return this.orderClient.getOrder(id).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.handleError();
        return throwError(() => error);
      })
    );
  }


  public getMyOrders(): Observable<BuyerOrderSummaryDTO[]> {
    this.spinnerService.showSpinner = true;
    return this.orderClient.getMyOrders().pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.handleError();
        return throwError(() => error);
      })
    );
  }


  public getSellerOrders(): Observable<SellerOrderSummaryDTO[]> {
    this.spinnerService.showSpinner = true;

    const sellerOrders$ = this.authService.isUserClientAdmin()
      ? this.orderClient.searchOrder([], 0, 1000, 'orderDate', SortDirection.Descending).pipe(
        map((result) =>
          result.items?.map((order: OrderLimitedDTO) => this.mapOrderLimitedToSellerSummary(order)) ?? []
        )
      )
      : this.orderClient.getSellerOrders();

    return sellerOrders$.pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        this.handleError();
        return throwError(() => error);
      })
    );
  }

  private mapOrderLimitedToSellerSummary(order: OrderLimitedDTO): SellerOrderSummaryDTO {
    return new SellerOrderSummaryDTO({
      id: order.orderId,
      orderNumber: order.orderNumber,
      orderDate: order.orderDate,
      totalAmount: order.totalAmount,
      status: order.status,
      itemCount: order.items?.length ?? 0,
      items: order.items ?? [],
      buyerId: order.buyerId,
      buyerName: order.buyerName,
      deliveryAddress: order.deliveryAddress,
      contactNumber: order.contactNumber,
      deliveryInstructions: order.deliveryInstructions
    });
  }

  public updateOrderStatus(data: UpdateOrderStatusDTO): Observable<OrderDTO> {
    this.spinnerService.showSpinner = true;
    return this.orderClient.updateOrderStatus(data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.orderStatusChanged$.next();
        this.toastService.showSuccess(
          'i18n.domain.order.message.update-title',
          'i18n.domain.order.message.update-success-message'
        );
      }),
      catchError((error: any) => {
        this.handleError();
        return throwError(() => error);
      })
    );
  }

  public cancelOrder(data: CancelOrderDTO): Observable<OrderDTO> {
    this.spinnerService.showSpinner = true;
    return this.orderClient.cancelOrder(data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.orderStatusChanged$.next();
        this.toastService.showSuccess(
          'i18n.domain.order.message.cancel-title',
          'i18n.domain.order.message.cancel-success-message'
        );
      }),
      catchError((error: any) => {
        this.handleError();
        return throwError(() => error);
      })
    );
  }

  public searchOrders(
    filters?: FilterDTO[],
    skip?: number,
    take?: number,
    sortBy?: string,
    sortDirection?: SortDirection
  ): Observable<PagedResultDTOOfOrderLimitedDTO> {
    this.spinnerService.showSpinner = true;

    return this.orderClient.searchOrder(filters, skip, take, sortBy, sortDirection).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  private handleError(): void {
    this.spinnerService.showSpinner = false;
    if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
      this.toastService.showError(
        'i18n.errors.server-error.title',
        'i18n.errors.server-error.message',
        null,
        ErrorType.serverError
      );
    }
  }
}
